import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Card } from '@consta/uikit/Card';
import { Text } from '@consta/uikit/Text';
import { Button } from '@consta/uikit/Button';
import { Table } from '@consta/uikit/Table';
import { Pagination } from '@consta/uikit/Pagination'; // Добавляем импорт
import { IconArrowLeft } from '@consta/icons/IconArrowLeft';
import { useNavigate, useParams } from 'react-router-dom';
import { useLazyGetReportQuery } from '../../../../services/reportService';
import { useLazyGetProjectCurrentStageInfoQuery } from '../../../../services/projectService';
import { Report } from '../../../../services/types/TReportServices';
import MonitoringStage2 from '../../../Modals/MonitoringStage2/MonitoringStage2';
import MonitoringStage3 from '../../../Modals/MonitoringStage3/MonitoringStage3';
import MonitoringStage4 from '../../../Modals/MonitoringStage4/MonitoringStage4';
import MonitoringStage5 from '../../../Modals/MonitoringStage5/MonitoringStage5';
import classes from './ReportsContent.module.scss';

const ReportsContent: React.FC = () => {
    const navigate = useNavigate();
    const { reportUuid } = useParams<{ reportUuid: string }>();
    const [triggerGetReport, { data: reportData, isLoading, error }] = useLazyGetReportQuery();
    const [getStage3Grouping, { data: stage3GroupingData }] = useLazyGetProjectCurrentStageInfoQuery();
    const [report, setReport] = useState<Report | null>(null);

    // Состояния для пагинации каждой таблицы
    const [stage2Page, setStage2Page] = useState(1);
    const [stage3Page, setStage3Page] = useState(1);
    const [stage4Page, setStage4Page] = useState(1);
    const [stage5Page, setStage5Page] = useState(1);

    const itemsPerPage = 20;

    // Состояния для модальных окон
    const [isStage2Open, setIsStage2Open] = useState(false);
    const [isStage3Open, setIsStage3Open] = useState(false);
    const [isStage4Open, setIsStage4Open] = useState(false);
    const [isStage5Open, setIsStage5Open] = useState(false);

    // Загружаем данные отчета
    useEffect(() => {
        if (reportUuid) {
            triggerGetReport({
                limit: 1,
                offset: 0,
                ordering: '-created_at'
            });
        }
    }, [reportUuid, triggerGetReport]);

    // Обрабатываем полученные данные
    useEffect(() => {
        if (reportData?.results?.[0]) {
            setReport(reportData.results[0]);
            // Сбрасываем страницы при загрузке нового отчета
            setStage2Page(1);
            setStage3Page(1);
            setStage4Page(1);
            setStage5Page(1);
        }
    }, [reportData]);

    // Загружаем данные группировки для этапа 3
    useEffect(() => {
        if (report?.project_info?.uuid) {
            getStage3Grouping({
                uuid: report.project_info.uuid,
                current_stage: 3
            });
        }
    }, [report, getStage3Grouping]);

    const handleBackToList = () => {
        navigate('/report');
    };

    // Функции для открытия модальных окон
    const handleOpenStage2 = () => setIsStage2Open(true);
    const handleOpenStage3 = () => setIsStage3Open(true);
    const handleOpenStage4 = () => setIsStage4Open(true);
    const handleOpenStage5 = () => setIsStage5Open(true);

    // Функции для закрытия модальных окон
    const handleCloseStage2 = () => setIsStage2Open(false);
    const handleCloseStage3 = () => setIsStage3Open(false);
    const handleCloseStage4 = () => setIsStage4Open(false);
    const handleCloseStage5 = () => setIsStage5Open(false);

    // Функция для преобразования даты
    const formatDate = (dateString: string) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('ru-RU');
        } catch (error) {
            return dateString;
        }
    };

    // Функция для получения данных текущей страницы
    const getPaginatedData = (data: any[], page: number) => {
        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return data.slice(startIndex, endIndex);
    };

    // Добавляем индексы к данным для отображения номера строки
    const addIndexToData = (data: any[], page: number) => {
        return data.map((item, idx) => ({
            ...item,
            rowIndex: (page - 1) * itemsPerPage + idx + 1
        }));
    };

    // Колонки таблицы скважин (только для чтения) - Этап 2
    const wellsColumns = [
        {
            title: '№',
            accessor: 'rowIndex',
            width: 60,
            renderCell: (row: any) => (
                <Text size="s">{row.rowIndex}</Text>
            )
        },
        {
            title: '№ скважины',
            accessor: 'number',
            width: 100,
            renderCell: (row: any) => (
                <Text size="s">{row.number}</Text>
            )
        },
        {
            title: 'Цех',
            accessor: 'workshop',
            width: 80,
            renderCell: (row: any) => (
                <Text size="s">{row.workshop || '-'}</Text>
            )
        },
        {
            title: 'Бригада',
            accessor: 'team',
            width: 80,
            renderCell: (row: any) => (
                <Text size="s">{row.team || '-'}</Text>
            )
        },
        {
            title: 'Участок',
            accessor: 'area_name',
            width: 120,
            renderCell: (row: any) => (
                <Text size="s">{row.area_name || '-'}</Text>
            )
        },
        {
            title: 'Горизонты',
            accessor: 'horizons_info',
            width: 120,
            renderCell: (row: any) => (
                <Text size="xs">
                    {row.horizons_info?.map((h: any) => h.code).join(', ') || '-'}
                </Text>
            )
        },
        {
            title: 'Пласты',
            accessor: 'layers_info',
            width: 120,
            renderCell: (row: any) => (
                <Text size="xs">
                    {row.layers_info?.map((l: any) => l.name).join(', ') || '-'}
                </Text>
            )
        },
        {
            title: 'Дебит жидкости',
            accessor: 'liquid_rate',
            width: 100,
            renderCell: (row: any) => (
                <Text size="s">{row.liquid_rate || '-'}</Text>
            )
        },
        {
            title: 'Q нефти',
            accessor: 'oil_rate',
            width: 80,
            renderCell: (row: any) => (
                <Text size="s">{row.oil_rate || '-'}</Text>
            )
        },
        {
            title: 'Обводненность (%)',
            accessor: 'water_cut',
            width: 100,
            renderCell: (row: any) => (
                <Text size="s">{row.water_cut || '-'}</Text>
            )
        },
        {
            title: 'Категория',
            accessor: 'category_name',
            width: 100,
            renderCell: (row: any) => (
                <Text size="s">{row.category_name || '-'}</Text>
            )
        }
    ];

    // Колонки для этапа 4 (сверка выполнения)
    const stage4Columns = [
        {
            title: '№ скважины',
            accessor: 'number',
            width: 100,
            renderCell: (row: any) => (
                <Text size="s">Скв. {row.number}</Text>
            )
        },
        {
            title: 'Проба отобрана',
            accessor: 'is_sample_taken',
            width: 100,
            renderCell: (row: any) => (
                <Text size="s">{row.is_sample_taken ? '✅' : '❌'}</Text>
            )
        },
        {
            title: 'Дата отбора',
            accessor: 'sampling_date',
            width: 100,
            renderCell: (row: any) => (
                <Text size="xs">{row.sampling_date ? formatDate(row.sampling_date) : '-'}</Text>
            )
        },
        {
            title: 'Получен образец нефти',
            accessor: 'is_oil_sample_received',
            width: 100,
            renderCell: (row: any) => (
                <Text size="s">{row.is_oil_sample_received ? '✅' : '❌'}</Text>
            )
        },
        {
            title: 'Объем нефти, мл',
            accessor: 'oil_volume',
            width: 100,
            renderCell: (row: any) => (
                <Text size="s">{row.oil_volume || '-'}</Text>
            )
        },
        {
            title: 'Получен образец воды',
            accessor: 'is_water_sample_received',
            width: 100,
            renderCell: (row: any) => (
                <Text size="s">{row.is_water_sample_received ? '✅' : '❌'}</Text>
            )
        },
        {
            title: 'Объем воды, мл',
            accessor: 'water_volume',
            width: 100,
            renderCell: (row: any) => (
                <Text size="s">{row.water_volume || '-'}</Text>
            )
        }
    ];

    // Колонки для этапа 5 (задание на исследование)
    const stage5Columns = [
        {
            title: '№ скважины',
            accessor: 'number',
            width: 100,
            renderCell: (row: any) => (
                <Text size="s">Скв. {row.number}</Text>
            )
        },
        {
            title: 'Категория',
            accessor: 'category_name',
            width: 100,
            renderCell: (row: any) => (
                <Text size="s">{row.category_name || '-'}</Text>
            )
        },
        {
            title: 'ГХ',
            accessor: 'is_gh',
            width: 60,
            renderCell: (row: any) => (
                <Text size="s">{row.is_gh ? '✅' : '❌'}</Text>
            )
        },
        {
            title: 'ICP',
            accessor: 'is_icp',
            width: 60,
            renderCell: (row: any) => (
                <Text size="s">{row.is_icp ? '✅' : '❌'}</Text>
            )
        },
        {
            title: 'ХАВ',
            accessor: 'is_hav',
            width: 60,
            renderCell: (row: any) => (
                <Text size="s">{row.is_hav ? '✅' : '❌'}</Text>
            )
        },
        {
            title: 'Капель',
            accessor: 'is_kapel',
            width: 60,
            renderCell: (row: any) => (
                <Text size="s">{row.is_kapel ? '✅' : '❌'}</Text>
            )
        }
    ];

    // Компонент для отображения типов скважин в заголовке
    const TypesDisplay = ({ types }: { types: string[] | null }) => {
        if (!types || types.length === 0) return null; // Ничего не отображаем, если типов нет
        if (types.length === 1) return <span>{types[0]}</span>;
        return <span>{types[0]} +{types.length - 1}</span>;
    };

    // Компонент для заголовка с тултипом
    // Компонент для заголовка с тултипом
    const HeaderWithTooltip = ({ group }: { group: any }) => {
        const [showTooltip, setShowTooltip] = useState(false);
        const [position, setPosition] = useState({ top: 0, left: 0 });
        const headerRef = useRef<HTMLDivElement>(null);

        const updatePosition = () => {
            if (headerRef.current) {
                const rect = headerRef.current.getBoundingClientRect();
                setPosition({
                    top: rect.bottom + window.scrollY + 5,
                    left: rect.left + window.scrollX,
                });
            }
        };

        const handleMouseEnter = () => {
            updatePosition();
            setShowTooltip(true);
        };

        const handleMouseLeave = () => {
            setShowTooltip(false);
        };

        // Определяем типы для отображения в тултипе
        const typesToDisplay = group?.config?.types?.length > 0
            ? group.config.types.join(', ')
            : 'Все типы скважин';

        return (
            <div
                ref={headerRef}
                style={{
                    position: 'relative',
                    textAlign: 'center',
                    cursor: 'help'
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <div style={{ fontWeight: 'bold' }}>
                    <TypesDisplay types={group.config.types} />
                </div>
                <div>{group.config.start}% - {group.config.end}%</div>
                <div>{group.config.volume} л</div>

                {showTooltip && createPortal(
                    <div style={{
                        position: 'absolute',
                        top: position.top,
                        left: position.left,
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        padding: '12px',
                        zIndex: 10000,
                        minWidth: '250px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
                        textAlign: 'left',
                    }}
                        onMouseEnter={() => setShowTooltip(true)}
                        onMouseLeave={() => setShowTooltip(false)}
                    >
                        <div style={{ marginBottom: '8px' }}>
                            <strong>Типы скважин:</strong>
                            <div style={{ marginTop: '4px', color: '#333' }}>
                                {typesToDisplay}
                            </div>
                        </div>
                        <div style={{ marginBottom: '4px' }}>
                            <strong>Обводненность:</strong> {group.config.start}% - {group.config.end}%
                        </div>
                        <div>
                            <strong>Объем:</strong> {group.config.volume} л
                        </div>
                    </div>,
                    document.body
                )}
            </div>
        );
    };

    // Функция для создания кастомной таблицы этапа 3
    const getCustomStage3TableData = () => {
        if (!stage3GroupingData?.groups) {
            return [];
        }

        // Находим максимальное количество скважин в группах
        const maxWellsCount = Math.max(
            ...stage3GroupingData.groups.map((group: any) => group.wells?.length || 0)
        );

        const rows = [];

        // Создаем строки для каждой скважины
        for (let i = 0; i < maxWellsCount; i++) {
            const row: any = {
                id: `row-${i}`,
            };

            // Для каждой группы добавляем номер скважины на позиции i
            stage3GroupingData.groups.forEach((group: any, groupIndex: any) => {
                const well = group.wells?.[i];
                row[`group${groupIndex}`] = well ? `Скважина ${well.number}` : '';
            });

            rows.push(row);
        }

        // Если нет скважин, показываем одну строку с "Нет данных"
        if (maxWellsCount === 0) {
            const emptyRow: any = {
                id: 'empty',
            };
            stage3GroupingData.groups.forEach((_: any, groupIndex: any) => {
                emptyRow[`group${groupIndex}`] = 'Нет данных';
            });
            return [emptyRow];
        }

        return rows;
    };

    // Создаем колонки для этапа 3 на основе групп
    const getStage3Columns = () => {
        if (!stage3GroupingData?.groups) {
            return [];
        }

        const columns: any = [];

        // Добавляем колонку для каждой группы
        stage3GroupingData.groups.forEach((group: any, index: any) => {
            columns.push({
                title: <HeaderWithTooltip group={group} />,
                accessor: `group${index}`,
                align: 'center',
                width: `${100 / stage3GroupingData.groups.length}%`,
                renderCell: (row: any) => (
                    <Text size="s">{row[`group${index}`]}</Text>
                )
            });
        });

        return columns;
    };

    // Получаем данные для каждой таблицы с пагинацией
    const wells = report?.project_info?.wells || [];
    const totalWells = wells.length;
    const totalPagesStage2 = Math.ceil(totalWells / itemsPerPage);

    const stage2PaginatedData = addIndexToData(
        getPaginatedData(wells, stage2Page),
        stage2Page
    );

    // Данные для кастомной таблицы этапа 3
    const stage3AllData = getCustomStage3TableData();
    const totalRowsStage3 = stage3AllData.length;
    const totalPagesStage3 = Math.ceil(totalRowsStage3 / itemsPerPage);

    const stage3PaginatedData = stage3AllData.slice(
        (stage3Page - 1) * itemsPerPage,
        stage3Page * itemsPerPage
    );

    const stage3CustomColumns = getStage3Columns();

    // Данные для этапа 4 с пагинацией
    const stage4PaginatedData = addIndexToData(
        getPaginatedData(wells, stage4Page),
        stage4Page
    );
    const totalPagesStage4 = Math.ceil(totalWells / itemsPerPage);

    // Данные для этапа 5 с пагинацией
    const stage5PaginatedData = addIndexToData(
        getPaginatedData(wells, stage5Page),
        stage5Page
    );
    const totalPagesStage5 = Math.ceil(totalWells / itemsPerPage);

    if (isLoading) {
        return (
            <div className={classes.pageContainer}>
                <div className={classes.loadingState}>
                    <Text>Загрузка отчета...</Text>
                </div>
            </div>
        );
    }

    if (error || !report) {
        return (
            <div className={classes.pageContainer}>
                <div className={classes.errorState}>
                    <Text view="warning">Ошибка загрузки отчета</Text>
                    <Text size="s" view="secondary">
                        Не удалось загрузить данные отчета
                    </Text>
                    <Button
                        label="Назад к списку"
                        onClick={handleBackToList}
                    />
                </div>
            </div>
        );
    }

    const projectInfo = report.project_info;

    return (
        <div className={classes.pageContainer}>
            {/* Заголовок страницы */}
            <div className={classes.pageHeader}>
                <div className={classes.titleSection}>
                    <Text size="2xl" weight="bold">
                        Отчет по мониторингу проекта №{projectInfo?.number}
                    </Text>
                    <Text size="l" view="secondary" className={classes.projectName}>
                        Проект: {projectInfo?.name}
                    </Text>
                    <Text size="s" view="secondary">
                        Дата создания отчета: {formatDate(report.created_at)}
                    </Text>
                </div>
                <Button
                    label="Назад к списку"
                    onClick={handleBackToList}
                    view='ghost'
                    size="m"
                    iconLeft={IconArrowLeft}
                />
            </div>

            {/* Основной контент со скроллом */}
            <div className={classes.scrollContainer}>
                <div className={classes.contentArea}>

                    {/* Основная информация о проекте */}
                    <Card verticalSpace="l" horizontalSpace="l" className={classes.card}>
                        <Text size="xl" weight="semibold" className={classes.cardTitle}>
                            Этап 1: Основные сведения
                        </Text>
                        <div className={classes.projectInfoGrid}>
                            <div className={classes.infoItem}>
                                <Text size="s" view="secondary">Номер проекта:</Text>
                                <Text weight="semibold">{projectInfo?.number || '-'}</Text>
                            </div>
                            <div className={classes.infoItem}>
                                <Text size="s" view="secondary">Наименование:</Text>
                                <Text weight="semibold">{projectInfo?.name || '-'}</Text>
                            </div>
                            <div className={classes.infoItem}>
                                <Text size="s" view="secondary">Компания:</Text>
                                <Text weight="semibold">{projectInfo?.company?.name || '-'}</Text>
                            </div>
                            <div className={classes.infoItem}>
                                <Text size="s" view="secondary">Период:</Text>
                                <Text weight="semibold">
                                    {projectInfo?.start_date ? formatDate(projectInfo.start_date) : '-'}
                                    {' - '}
                                    {projectInfo?.end_date ? formatDate(projectInfo.end_date) : '-'}
                                </Text>
                            </div>
                            <div className={classes.infoItem}>
                                <Text size="s" view="secondary">Месторождения:</Text>
                                <Text weight="semibold">
                                    {projectInfo?.fields?.map((f: any) => f.name).join(', ') || '-'}
                                </Text>
                            </div>
                            <div className={classes.infoItem}>
                                <Text size="s" view="secondary">Залежи:</Text>
                                <Text weight="semibold">
                                    {projectInfo?.deposits?.map((d: any) => d.name).join(', ') || '-'}
                                </Text>
                            </div>
                            <div className={classes.infoItem}>
                                <Text size="s" view="secondary">Участки:</Text>
                                <Text weight="semibold">
                                    {projectInfo?.areas?.map((a: any) => a.name).join(', ') || '-'}
                                </Text>
                            </div>
                            <div className={classes.infoItem}>
                                <Text size="s" view="secondary">Всего скважин:</Text>
                                <Text weight="semibold">{totalWells}</Text>
                            </div>
                        </div>

                        {/* Описание проекта */}
                        {projectInfo?.description && (
                            <div className={classes.descriptionSection}>
                                <Text size="s" view="secondary">Описание проекта:</Text>
                                <Text size="s">{projectInfo.description}</Text>
                            </div>
                        )}
                    </Card>

                    {/* Этап 2: Работа со скважинами */}
                    <Card verticalSpace="l" horizontalSpace="l" className={classes.card}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <Text size="xl" weight="semibold" className={classes.cardTitle}>
                                Этап 2: Работа со скважинами
                            </Text>
                            <Button
                                size="s"
                                view="secondary"
                                label="Скачать отчет"
                                onClick={handleOpenStage2}
                            />
                        </div>
                        {wells.length > 0 ? (
                            <>
                                <div className={classes.tableContainer}>
                                    <Table
                                        columns={wellsColumns}
                                        rows={stage2PaginatedData}
                                        stickyHeader
                                        verticalAlign="top"
                                        borderBetweenRows
                                        borderBetweenColumns
                                        size="s"
                                    />
                                </div>
                                {totalPagesStage2 > 1 && (
                                    <div className={classes.paginationSection} style={{
                                        marginTop: '20px',
                                        display: 'flex',
                                        justifyContent: 'center'
                                    }}>
                                        <Pagination
                                            items={totalPagesStage2}
                                            value={stage2Page}
                                            onChange={setStage2Page}
                                            showFirstPage
                                            showLastPage
                                            visibleCount={7}
                                            size='s'
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <Text view="secondary" style={{ textAlign: 'center', padding: '20px' }}>
                                Нет данных о скважинах
                            </Text>
                        )}
                    </Card>

                    {/* Этап 3: Группировка скважин - КАСТОМНАЯ ТАБЛИЦА */}
                    <Card verticalSpace="l" horizontalSpace="l" className={classes.card}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <Text size="xl" weight="semibold" className={classes.cardTitle}>
                                Этап 3: Группировка скважин по обводненности
                            </Text>
                            <Button
                                size="s"
                                view="secondary"
                                label="Создать этикетку"
                                onClick={handleOpenStage3}
                            />
                        </div>
                        {stage3PaginatedData.length > 0 ? (
                            <>
                                <div className={classes.tableContainer}>
                                    <Table
                                        columns={stage3CustomColumns}
                                        rows={stage3PaginatedData}
                                        stickyHeader
                                        verticalAlign="top"
                                        borderBetweenRows
                                        borderBetweenColumns
                                        size="s"
                                    />
                                </div>
                                {totalPagesStage3 > 1 && (
                                    <div className={classes.paginationSection} style={{
                                        marginTop: '20px',
                                        display: 'flex',
                                        justifyContent: 'center'
                                    }}>
                                        <Pagination
                                            items={totalPagesStage3}
                                            value={stage3Page}
                                            onChange={setStage3Page}
                                            showFirstPage
                                            showLastPage
                                            visibleCount={7}
                                            size='s'
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <Text view="secondary" style={{ textAlign: 'center', padding: '20px' }}>
                                Нет данных для группировки
                            </Text>
                        )}
                    </Card>

                    {/* Этап 4: Сверка выполнения */}
                    <Card verticalSpace="l" horizontalSpace="l" className={classes.card}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <Text size="xl" weight="semibold" className={classes.cardTitle}>
                                Этап 4: Сверка выполнения программы отбора проб
                            </Text>
                            <Button
                                size="s"
                                view="secondary"
                                label="Скачать отчет"
                                onClick={handleOpenStage4}
                            />
                        </div>
                        <div className={classes.stageInfo}>
                            <div className={classes.stageStats}>
                                <div className={classes.statItem}>
                                    <Text size="s" view="secondary">Образцов нефти:</Text>
                                    <Text size="s" weight="semibold">{wells.filter((w: any) => w.is_oil_sample_received).length}</Text>
                                </div>
                                <div className={classes.statItem}>
                                    <Text size="s" view="secondary">Образцов воды:</Text>
                                    <Text size="s" weight="semibold">{wells.filter((w: any) => w.is_water_sample_received).length}</Text>
                                </div>
                            </div>
                        </div>
                        {wells.length > 0 ? (
                            <>
                                <div className={classes.tableContainer}>
                                    <Table
                                        columns={stage4Columns}
                                        rows={stage4PaginatedData}
                                        verticalAlign="top"
                                        stickyHeader
                                        borderBetweenRows
                                        borderBetweenColumns
                                        size="s"
                                    />
                                </div>
                                {totalPagesStage4 > 1 && (
                                    <div className={classes.paginationSection} style={{
                                        marginTop: '20px',
                                        display: 'flex',
                                        justifyContent: 'center'
                                    }}>
                                        <Pagination
                                            items={totalPagesStage4}
                                            value={stage4Page}
                                            onChange={setStage4Page}
                                            showFirstPage
                                            showLastPage
                                            visibleCount={7}
                                            size='s'
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <Text view="secondary" style={{ textAlign: 'center', padding: '20px' }}>
                                Нет данных для отображения
                            </Text>
                        )}
                    </Card>

                    {/* Этап 5: Задание на исследование */}
                    <Card verticalSpace="l" horizontalSpace="l" className={classes.card}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <Text size="xl" weight="semibold" className={classes.cardTitle}>
                                Этап 5: Задание на исследование
                            </Text>
                            <Button
                                size="s"
                                view="secondary"
                                label="Скачать отчет"
                                onClick={handleOpenStage5}
                            />
                        </div>
                        <div className={classes.stageInfo}>
                            <div className={classes.stageStats}>
                                <div className={classes.statItem}>
                                    <Text size="s" view="secondary">ГХ:</Text>
                                    <Text size="s" weight="semibold">{wells.filter((w: any) => w.is_gh).length}</Text>
                                </div>
                                <div className={classes.statItem}>
                                    <Text size="s" view="secondary">ICP:</Text>
                                    <Text size="s" weight="semibold">{wells.filter((w: any) => w.is_icp).length}</Text>
                                </div>
                                <div className={classes.statItem}>
                                    <Text size="s" view="secondary">ХАВ:</Text>
                                    <Text size="s" weight="semibold">{wells.filter((w: any) => w.is_hav).length}</Text>
                                </div>
                                <div className={classes.statItem}>
                                    <Text size="s" view="secondary">Капель:</Text>
                                    <Text size="s" weight="semibold">{wells.filter((w: any) => w.is_kapel).length}</Text>
                                </div>
                            </div>
                        </div>
                        {wells.length > 0 ? (
                            <>
                                <div className={classes.tableContainer}>
                                    <Table
                                        columns={stage5Columns}
                                        rows={stage5PaginatedData}
                                        verticalAlign="top"
                                        borderBetweenRows
                                        stickyHeader
                                        borderBetweenColumns
                                        size="s"
                                    />
                                </div>
                                {totalPagesStage5 > 1 && (
                                    <div className={classes.paginationSection} style={{
                                        marginTop: '20px',
                                        display: 'flex',
                                        justifyContent: 'center'
                                    }}>
                                        <Pagination
                                            items={totalPagesStage5}
                                            value={stage5Page}
                                            onChange={setStage5Page}
                                            showFirstPage
                                            showLastPage
                                            visibleCount={7}
                                            size='s'
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <Text view="secondary" style={{ textAlign: 'center', padding: '20px' }}>
                                Нет данных для отображения
                            </Text>
                        )}
                    </Card>

                    {/* Добавляем пустое место внизу */}
                    <div style={{ height: '100px' }}></div>
                </div>
            </div>

            {/* Модальные окна для скачивания отчетов */}
            <MonitoringStage2
                isOpen={isStage2Open}
                setIsOpen={setIsStage2Open}
                handleCloseModal={handleCloseStage2}
                projectUuid={projectInfo?.uuid}
            />

            <MonitoringStage3
                isOpen={isStage3Open}
                setIsOpen={setIsStage3Open}
                handleCloseModal={handleCloseStage3}
                projectUuid={projectInfo?.uuid}
            />

            <MonitoringStage4
                isOpen={isStage4Open}
                setIsOpen={setIsStage4Open}
                handleCloseModal={handleCloseStage4}
                projectUuid={projectInfo?.uuid}
            />

            <MonitoringStage5
                isOpen={isStage5Open}
                setIsOpen={setIsStage5Open}
                handleCloseModal={handleCloseStage5}
                projectUuid={projectInfo?.uuid}
            />
        </div>
    );
};

export default ReportsContent;